
import { Component, Vue, Ref } from "vue-property-decorator";
import ManualConsolidationWizardModal from "@/components/widgets/Modals/ManualConsolidationWizardModal.vue";
import notifications from "../lib/notifications";
import {
  OrderShipsWithSuggestionsResponse,
  AddOrderToOrderShipsWithRequest,
  AddOrderToOrderShipsWithResponse,
  ResetShipsWithOrderResponse
} from "../models/api/orders/shipsWith";
import { getModule } from "vuex-module-decorators";
import { OrderModule } from "@/store/modules/index";
import { IOrderResource } from "@/store/types";
import { OrderListItemResource, OrderResource } from "@/models/api/orders/get";

const orderModule = getModule(OrderModule);

@Component({
  components: {
    ManualConsolidationWizardModal
  }
})
export default class ShipsWithOrders extends Vue {
  loading: boolean = false;
  hasOrder: boolean = false;
  suggestionsResponse: OrderShipsWithSuggestionsResponse = null;
  orderNumbersToConsolidate: number[] = [];
  orderNumbersToUnconsolidate: number[] = [];
  saving: boolean = false;
  unconsolidating: boolean = false;
  hasError: boolean = false;
  suggestionsLoaded: boolean = false;

  @Ref() readonly manualConsolidationModal!: ManualConsolidationWizardModal;

  async created() {
    await this.tryLoadWithoutSuggestions(); 
  }

  async tryLoadWithoutSuggestions()
  {
    this.loading = true;
    try{  
      await orderModule.getOrder(this.orderNumber);
      await orderModule.getOrderShipsWithOrders(orderModule.currentOrder);

    } catch(ex)
    {
      this.hasError = true;    
      notifications.error("Ships with Orders", "Failed to load ships with orders", ex);
    }

    this.loading = false;
  }

  async tryLoadSuggestions() {
    this.loading = true;

    try {
      this.suggestionsResponse = await orderModule.getOrderShipsWithOrdersSuggestions(
        this.orderNumber
      );
      this.suggestionsLoaded = true;
      this.hasOrder = true;
      this.hasError = false;
    } catch (ex) {
      this.suggestionsLoaded = false;
      this.hasError = true;
      notifications.error("Ships with Orders", "Failed to load order suggestions", ex);
    }

    this.loading = false;
  }

  updateOrdersToConsolidate({ item, selected }) {
    if (selected) this.orderNumbersToConsolidate.push(item.headerNumber);
    else {
      const indexToRemove = this.orderNumbersToConsolidate.findIndex(x => x === item.number);
      this.orderNumbersToConsolidate.splice(indexToRemove, 1);
    }
  }

  showManualModal() {
    this.manualConsolidationModal.show();
  }

  handleUnconsolidateSelected({ item, selected }) {
    if (selected) this.orderNumbersToUnconsolidate.push(item.headerNumber);
    else {
      const indexToRemove = this.orderNumbersToUnconsolidate.findIndex(x => x === item.number);
      this.orderNumbersToUnconsolidate.splice(indexToRemove, 1);
    }
  }

  async unconsolidate() {
    if (!this.orderNumbersToUnconsolidate) return;
    this.unconsolidating = true;

    for (let index = 0; index < this.orderNumbersToUnconsolidate.length; index++) {
      const orderNumberToUnconsolidate = this.orderNumbersToUnconsolidate[index];

      try {
        await orderModule.resetShipsWithOrder(orderNumberToUnconsolidate);
        notifications.success(
          "Order Unconsolidated",
          `Order ${orderNumberToUnconsolidate} successfully unconsolidated.`
        );
      } catch (ex) {
        notifications.error(
          "Order Unconsolidated",
          `Order ${orderNumberToUnconsolidate} not unconsolidated.`,
          ex
        );
      }
    }

    this.unconsolidating = false;
    this.orderNumbersToUnconsolidate = [];
    if(this.suggestionsLoaded)
      await this.tryLoadSuggestions();
    else
      await this.tryLoadWithoutSuggestions();
  }

  async consolidate() {
    if (!this.orderNumbersToConsolidate) return;

    this.saving = true;

    for (let index = 0; index < this.orderNumbersToConsolidate.length; index++) {
      const orderNumberToConsolidate = this.orderNumbersToConsolidate[index];
      try {
        await orderModule.addOrderToShipsWithOrder({
          childOrderNumber: orderNumberToConsolidate,
          shipWithOrderHeaderNumber: this.orderNumber,
          existingShipsWithOrdersPromptResult: true
        });
        this.migrateConsolidatedToShipsWithList(orderNumberToConsolidate);
        notifications.success(
          "Order Consolidated",
          `Order ${orderNumberToConsolidate} successfully consolidated.`
        );
      } catch (ex) {
        notifications.error(
          "Order Consolidation",
          `Order ${orderNumberToConsolidate} not consolidated.`,
          ex
        );
      }
    }

    this.saving = false;
  }

  migrateConsolidatedToShipsWithList(headerNumber: number) {
    let existingRecord = this.suggestionsResponse.exactMatches.find(
      x => x.headerNumber == headerNumber
    );

    if (existingRecord) {
      this.suggestionsResponse.shipsWithOrders.push(existingRecord);
      this.suggestionsResponse.exactMatches.splice(
        this.suggestionsResponse.exactMatches.findIndex(x => x.headerNumber === headerNumber),
        1
      );
      return;
    }

    existingRecord = this.suggestionsResponse.possibleMatches.find(
      x => x.headerNumber == headerNumber
    );

    if (existingRecord) {
      this.suggestionsResponse.shipsWithOrders.push(existingRecord);
      this.suggestionsResponse.possibleMatches.splice(
        this.suggestionsResponse.possibleMatches.findIndex(x => x.headerNumber === headerNumber)
      );
    }
  }

  get currentOrder(): IOrderResource {
    return orderModule.currentOrder;
  }

  get orderNumber(): number {
    return +this.$route.params.idx;
  }

  get canConsolidate() {
    return this.orderNumbersToConsolidate && this.orderNumbersToConsolidate.length > 0;
  }

  get canUnconsolidate() {
    return this.orderNumbersToUnconsolidate && this.orderNumbersToUnconsolidate.length > 0;
  }

  get consigneeAddress() {
    if (!this.currentOrder.consignee.address) {
      return "";
    }

    let address = this.currentOrder.consignee.address.streetLine1;

    if (this.currentOrder.consignee.address.streetLine2)
      address += " " + this.currentOrder.consignee.address.streetLine2;

    return `${address} ${this.currentOrder.consignee.address.city} ${this.currentOrder.consignee.address.state} ${this.currentOrder.consignee.address.zip}`;
  }

  get shipsWithOrders() {
    return this.suggestionsResponse ? this.suggestionsResponse.shipsWithOrders : JSON.parse(JSON.stringify(orderModule.currentOrderShipsWithOrders))
  }
}
